import axios from 'axios';
import { API_CONFIG } from '../config/env';

interface ExchangeRateResponse {
  result: string;
  base_code: string;
  conversion_rates: Record<string, number>;
  time_last_update_unix: number;
}

interface CachedRates {
  rates: Record<string, number>;
  timestamp: number;
}

const CACHE_KEY = 'exchange_rates_cache';
const CACHE_DURATION = 6 * 60 * 60 * 1000; // 6 hours in milliseconds
/** Base minimum in USD — lowered for PaymentEscrow MetaMask tests (0.5 USDC). */
export const USD_MINIMUM = 0.5;

class ExchangeRateService {
  private getCache(): CachedRates | null {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) return null;
      
      const parsed: CachedRates = JSON.parse(cached);
      return parsed;
    } catch (error) {
      console.error('Error reading exchange rate cache:', error);
      return null;
    }
  }

  private setCache(rates: Record<string, number>): void {
    try {
      const cache: CachedRates = {
        rates,
        timestamp: Date.now(),
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
      console.error('Error saving exchange rate cache:', error);
    }
  }

  private isCacheValid(cache: CachedRates): boolean {
    const now = Date.now();
    return (now - cache.timestamp) < CACHE_DURATION;
  }

  async getExchangeRates(): Promise<Record<string, number>> {
    // Check cache first
    const cached = this.getCache();
    if (cached && this.isCacheValid(cached)) {
      return cached.rates;
    }

    // Try to fetch fresh rates
    try {
      const response = await axios.get<ExchangeRateResponse>(
        `https://v6.exchangerate-api.com/v6/${API_CONFIG.EXCHANGE_API_KEY}/latest/USD`
      );

      if (response.data.result === 'success' && response.data.conversion_rates) {
        this.setCache(response.data.conversion_rates);
        return response.data.conversion_rates;
      } else {
        throw new Error('Invalid response from exchange rate API');
      }
    } catch (error) {
      console.error('Error fetching exchange rates:', error);
      
      // Fall back to cached data if available (even if expired)
      if (cached && cached.rates) {
        console.warn('Using expired cache due to API error');
        return cached.rates;
      }
      
      // Last resort: return USD rate only
      console.warn('No cache available, using USD only');
      return { USD: 1 };
    }
  }

  /**
   * Get the minimum amount for a given currency (equivalent to USD_MINIMUM USD)
   * @param currency - The currency code (e.g., 'USD', 'EUR', 'GBP')
   * @returns Promise<number> - The minimum amount in the specified currency
   */
  async getMinimumAmount(currency: string): Promise<number> {
    if (!currency) return USD_MINIMUM;
    
    const upperCurrency = currency.toUpperCase();
    
    // If USD, return directly
    if (upperCurrency === 'USD') {
      return USD_MINIMUM;
    }

    try {
      const rates = await this.getExchangeRates();
      const rate = rates[upperCurrency];
      
      if (!rate || rate <= 0) {
        console.warn(`No exchange rate found for ${upperCurrency}, using USD minimum`);
        return USD_MINIMUM;
      }

      // Convert USD_MINIMUM to the target currency (rate = units of target per 1 USD)
      const minimum = USD_MINIMUM * rate;
      
      // Round to 2 decimal places
      return Math.round(minimum * 100) / 100;
    } catch (error) {
      console.error(`Error calculating minimum for ${currency}:`, error);
      return USD_MINIMUM;
    }
  }

  /**
   * Format the minimum amount for display
   * @param currency - The currency code
   * @param amount - The minimum amount
   * @returns Formatted string (e.g., "25.00 USD" or "21.47 EUR")
   */
  formatMinimum(currency: string, amount: number): string {
    return `${amount.toFixed(2)} ${currency.toUpperCase()}`;
  }
}

export const exchangeRateService = new ExchangeRateService();

